import Button from 'components/Button';
import DjangoMessages from 'components/DjangoMessages';
import FormResult from 'components/Form/FormResult';
import Information from 'components/Information';
import Link from 'components/Link';
import LinkButton from 'components/LinkButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import TablePagination from 'components/Table/TableComponents/TablePagination';
import Table from 'components/Table/TableStatic';
import Truncator from 'components/Truncator';
import {FC} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {jt, t} from 'ttag';
import {jstDateString, jstDateTimeString} from 'utils/date';
import updateUrlParamsAndReload from 'utils/pagination';

import ExportHistoryDataModal, {
    useExportHistoryDataModal,
} from '../components/ExportModal';
import Filter from './components/Filter';
import {DocumentArchiveBackendData, FilterFormData} from './types';

const Events: FC = () => {
    const {
        appPermissions,
        data: {archivedDocuments = [], defaultFilteredValues, pagination},
    } = useBackend<DocumentArchiveBackendData>();

    const modalProps = useExportHistoryDataModal('document-archive');

    const methods = useForm<FilterFormData>({
        defaultValues: {
            amount_begin: defaultFilteredValues.amountBegin,
            amount_end: defaultFilteredValues.amountEnd,
            client: defaultFilteredValues.client,
            date_issued_begin: defaultFilteredValues.dateIssuedBegin,
            date_issued_end: defaultFilteredValues.dateIssuedEnd,
            date_sent_begin: defaultFilteredValues.dateSentBegin,
            date_sent_end: defaultFilteredValues.dateSentEnd,
            delivery_methods: defaultFilteredValues.deliveryMethods,
            document_type: defaultFilteredValues.documentType,
            employee: defaultFilteredValues.employee,
            external_id: defaultFilteredValues.externalId,
            item_type: defaultFilteredValues.itemType,
            legal_categories: defaultFilteredValues.legalCategories,
            search: defaultFilteredValues.search,
        },
    });

    const learnMoreLink = (
        <Link
            href="https://help.makeleaps.jp/ja/articles/5614653-%E7%99%BA%E8%A1%8C%E6%9B%B8%E9%A1%9E%E6%8E%A7%E3%81%88%E3%82%92%E7%A2%BA%E8%AA%8D%E3%81%99%E3%82%8B?segment_aid=e4529fab-d30e-428f-b02a-5ff39abca405"
            isOpenInNewTab={true}
        >{t`Learn More (Japanese)`}</Link>
    );

    return (
        <FormProvider {...methods}>
            <FormResult />

            <div className="flex flex-col gap-2">
                <DjangoMessages />
                <NavigationTabs.History
                    permissions={appPermissions}
                    selection="documentArchive"
                />
                <p>
                    {jt`Copies of
                    your documents archived for legal purposes when critical
                    actions are performed (such as sending). ${learnMoreLink}`}
                </p>
                <Filter />
                {archivedDocuments.length === 0 ? (
                    <Information>{t`There are no archived documents.`}</Information>
                ) : (
                    <>
                        <Table className="min-w-fit" hasStripes={true}>
                            <Table.Header className="text-nowrap">
                                <Table.HeaderRow>
                                    <Table.Column className="min-w-36">{t`Archived at`}</Table.Column>
                                    <Table.Column>{t`Document`}</Table.Column>
                                    <Table.Column>{t`(Transaction) Date`}</Table.Column>
                                    <Table.Column>{t`Client`}</Table.Column>
                                    <Table.Column className="text-right">{t`Total`}</Table.Column>
                                    <Table.Column className="flex justify-end">
                                        <Button
                                            className="font-normal"
                                            kind="secondary"
                                            onClick={() =>
                                                modalProps.setIsModalOpen(true)
                                            }
                                            size="xs"
                                        >{t`Export`}</Button>
                                    </Table.Column>
                                </Table.HeaderRow>
                            </Table.Header>
                            <Table.Body>
                                {archivedDocuments.map((document) => {
                                    return (
                                        document && (
                                            <Table.Row key={document.mid}>
                                                <Table.Cell>
                                                    {jstDateTimeString(
                                                        document.archivedDate,
                                                    )}
                                                </Table.Cell>
                                                <Table.Cell className="min-w-28 max-w-32">
                                                    <Truncator
                                                        text={document.label}
                                                        truncateAt="middle"
                                                    />
                                                </Table.Cell>
                                                <Table.Cell>
                                                    {jstDateString(
                                                        document.transactionDate,
                                                    )}
                                                </Table.Cell>
                                                <Table.Cell className="min-w-28 max-w-32">
                                                    <Truncator
                                                        text={document.client}
                                                        truncateAt="middle"
                                                    />
                                                </Table.Cell>
                                                <Table.Cell className="max-w-28">
                                                    <Truncator
                                                        isRightAligned={true}
                                                        text={document.total}
                                                        truncateAt="middle"
                                                    />
                                                </Table.Cell>
                                                <Table.Cell className="flex justify-end">
                                                    <LinkButton
                                                        href={window.Urls.preservation_item_view(
                                                            document.mid,
                                                        )}
                                                        kind="secondary"
                                                        size="xs"
                                                    >
                                                        {t`Details`}
                                                    </LinkButton>
                                                </Table.Cell>
                                            </Table.Row>
                                        )
                                    );
                                })}
                            </Table.Body>
                        </Table>
                        <TablePagination
                            className="mb-5"
                            hideForSinglePage={true}
                            isAmbigousStop={true}
                            onChange={(page) =>
                                updateUrlParamsAndReload('page', page)
                            }
                            onFirst={() => updateUrlParamsAndReload('page', 1)}
                            onLast={() =>
                                updateUrlParamsAndReload('page', 'last')
                            }
                            pageCurrent={pagination.currentPage}
                            pageStop={pagination.numPages}
                            paginationPadding={pagination.windowPages}
                        />
                    </>
                )}
            </div>
            <ExportHistoryDataModal {...modalProps} />
        </FormProvider>
    );
};
export default Events;
